// client/src/coms/organisation/storageAccounts.js
// Connecting storage accounts with the provider's own sign-in. Voxemera never asks for provider passwords:
// the sign-in happens on the provider's page in a popup, which reports back to this window (same origin only).
// Also: the Google Picker loader, and the account card shown wherever connected accounts are listed.
import React from 'react';
import { apiGetJson, apiDelete } from './orgApi';
import { ProviderBadge, notify } from './orgShared';

// Opens the popup synchronously (inside the click), asks Voxemera for the provider's sign-in address, then
// sends the popup there. Errors from Voxemera (not allowed, not available) are shown here instead of in the
// popup. Resolves with the callback's result: { ok, connection_id, account, reconnected } or { ok: false, error }.
export function connectAccount(provider, { mode = 'read', purpose = 'personal', server = '', reconnect = null } = {}) {
  const w = window.open('about:blank', `vox-storage-${provider}`, 'width=560,height=720');
  if (!w) return Promise.resolve({ ok: false, error: 'Your browser blocked the sign-in window. Allow pop-ups for Voxemera and try again.' });
  try {
    w.document.title = 'Connecting…';
    w.document.body.style.cssText = 'font-family:system-ui;background:#1e1f22;color:#eee;padding:24px';
    w.document.body.textContent = 'Opening the provider’s sign-in page…';
  } catch {}
  const qs = new URLSearchParams({ format: 'json', mode, purpose, ...(server ? { server } : {}), ...(reconnect ? { reconnect: String(reconnect) } : {}) });
  return new Promise((resolve) => {
    let settled = false;
    const finish = (r) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMsg);
      clearInterval(timer);
      resolve(r);
    };
    const onMsg = (e) => {
      if (e.origin !== window.location.origin || !e.data || e.data.type !== 'vox-storage-oauth') return;
      finish(e.data.result || { ok: false });
    };
    window.addEventListener('message', onMsg);
    const timer = setInterval(() => {
      if (w.closed) setTimeout(() => finish({ ok: false, closed: true }), 400);
    }, 600);
    apiGetJson(`/storage/oauth/${provider}/start?${qs}`).then(
      (r) => {
        try {
          w.location.href = r.url;
        } catch {
          finish({ ok: false, error: 'The sign-in window could not be opened' });
        }
      },
      (e) => {
        try {
          w.close();
        } catch {}
        finish({ ok: false, error: e.message });
      }
    );
  });
}

// Shows the outcome of a sign-in as a toast; returns the result.
export async function connectWithFeedback(provider, label, opts) {
  const r = await connectAccount(provider, opts);
  if (r.ok) notify(r.reconnected ? `Reconnected ${label}${r.account ? ` (${r.account})` : ''}` : `Connected ${label}${r.account ? ` as ${r.account}` : ''}`, { tone: 'success' });
  else if (!r.closed) notify(r.error || 'The connection did not complete', { tone: 'error' });
  return r;
}

// ---- Google Picker ----
let gapiLoading = null;
function loadPicker() {
  if (window.google && window.google.picker) return Promise.resolve();
  if (!gapiLoading) {
    gapiLoading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://apis.google.com/js/api.js';
      s.async = true;
      s.onload = () => window.gapi.load('picker', { callback: resolve, onerror: () => reject(new Error('Google’s file picker could not be loaded')) });
      s.onerror = () => {
        gapiLoading = null;
        reject(new Error('Google’s file picker could not be loaded (check your connection or content blockers)'));
      };
      document.head.appendChild(s);
    });
  }
  return gapiLoading;
}

// Google's Picker adds its own backdrop and dialog to <body> with Google's stacking order (about 1000), while
// Voxemera's modal layer (.vxo-backdrop, portalled to <body>) sits at 4000, so a Picker opened from a Voxemera
// dialog appeared underneath it. Rather than outbidding Google's z-index, Voxemera's modal layer steps aside
// while the Picker is open: it is hidden, made inert (no clicks, focus or keyboard trap), and restored with
// focus returned to where it was once the Picker closes (chosen, cancelled or failed).
let pickerOpen = false;
export function isExternalPickerOpen() {
  return pickerOpen;
}
function suspendModalLayer() {
  const prevFocus = document.activeElement;
  const layers = Array.from(document.querySelectorAll('.vxo-backdrop'));
  for (const el of layers) el.setAttribute('inert', '');
  document.body.classList.add('vx-external-picker');
  pickerOpen = true;
  let restored = false;
  return () => {
    if (restored) return;
    restored = true;
    pickerOpen = false;
    document.body.classList.remove('vx-external-picker');
    for (const el of layers) el.removeAttribute('inert');
    if (prevFocus && prevFocus.isConnected && prevFocus.focus) prevFocus.focus();
  };
}

// grant: { access_token, api_key, app_id } from Voxemera. Resolves with the chosen file ids ([] when cancelled).
// Google then gives Voxemera access to exactly those files (drive.file). A chosen folder is linked as a folder
// (it opens in Google Drive); its contents are not shared with Voxemera unless they are chosen too.
export async function pickDriveFiles(grant, { title = 'Choose files to use in Voxemera' } = {}) {
  await loadPicker();
  const g = window.google;
  const resume = suspendModalLayer();
  return new Promise((resolve, reject) => {
    const docs = new g.picker.DocsView(g.picker.ViewId.DOCS).setIncludeFolders(true).setSelectFolderEnabled(true);
    let builder = new g.picker.PickerBuilder()
      .addView(docs)
      .addView(new g.picker.DocsView(g.picker.ViewId.RECENTLY_PICKED))
      .addView(new g.picker.DocsUploadView())
      .enableFeature(g.picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(grant.access_token)
      .setAppId(grant.app_id)
      .setOrigin(`${window.location.protocol}//${window.location.host}`);
    if (grant.api_key) builder = builder.setDeveloperKey(grant.api_key);
    // Each opening builds a new picker; the closed one is disposed so hidden pickers do not pile up in <body>.
    const close = () => {
      resume();
      setTimeout(() => {
        try {
          picker.dispose();
        } catch {}
      }, 0);
    };
    const picker = builder
      .setTitle(title)
      .setCallback((data) => {
        const action = data[g.picker.Response.ACTION];
        if (action === g.picker.Action.PICKED) {
          close();
          resolve((data[g.picker.Response.DOCUMENTS] || []).map((d) => d[g.picker.Document.ID]));
        } else if (action === g.picker.Action.CANCEL) {
          close();
          resolve([]);
        } else if (action === 'error') {
          close();
          reject(new Error('Google’s file picker could not open. Try again, or reconnect Google Drive.'));
        }
      })
      .build();
    try {
      picker.setVisible(true);
    } catch (e) {
      resume();
      reject(e);
    }
  });
}

// Opens a tab inside the click (so pop-up blockers allow it), then sends it to the URL Voxemera returns
// ({ url }). The tab is closed again if Voxemera refuses; the error is re-thrown for the caller to show.
export async function openWhenReady(promise) {
  const w = window.open('', '_blank');
  try {
    if (w) w.opener = null;
  } catch {}
  try {
    const r = await promise;
    if (!r || !r.url) throw new Error('No link was returned');
    if (w) w.location.href = r.url;
    else window.open(r.url, '_blank', 'noopener,noreferrer');
    return r;
  } catch (e) {
    try {
      if (w) w.close();
    } catch {}
    throw e;
  }
}

// Download choices for a Drive item: Google's own links (binary download, or exports of Docs/Sheets/Slides).
export function downloadActions(it, open) {
  return (it.downloads || []).map((d) => ({ label: d.label, onClick: () => open(d) }));
}

export async function disconnectAccount(conn) {
  const r = await apiDelete(`/me/storage/connections/${conn.id}`);
  notify(r.note || 'Disconnected', { tone: 'success' });
  return r;
}

// One connected account: who, status, what Voxemera may do, and the actions.
export function AccountCard({ conn, onBrowse, onReconnect, onDisconnect, compact = false }) {
  const bad = conn.status !== 'ok';
  return (
    <div className={`vxo-account${bad ? ' is-warn' : ''}${compact ? ' is-compact' : ''}`} data-testid={`account-${conn.provider}`}>
      <div className="vxo-account-head">
        <ProviderBadge provider={conn.provider} />
        <div className="vxo-account-who">
          <strong>{conn.account_label || conn.provider_label}</strong>
          <span className="vxo-muted">{conn.provider_label}</span>
        </div>
        <span className={`vxo-pill${bad ? ' is-warn' : ' is-ok'}`}>{conn.status_text}</span>
      </div>
      {conn.needs_reconnect ? <p className="vxo-warn-text">{conn.provider_label} no longer accepts this sign-in (it expired or access was removed). Reconnect to keep using it.</p> : null}
      {!compact ? (
        <>
          <ul className="vxo-perm-list" aria-label="What Voxemera may do">
            {(conn.permissions || []).map((p) => (
              <li key={p}>{p}</li>
            ))}
            <li>{conn.mode === 'readwrite' ? 'You allowed uploads and changes through Voxemera' : 'Read-only in Voxemera'}</li>
          </ul>
        </>
      ) : null}
      <div className="vxo-actions">
        {conn.browsable && onBrowse && !conn.needs_reconnect ? (
          <button type="button" className="vx-btn small primary" onClick={() => onBrowse(conn)} data-testid="account-browse">
            Browse files
          </button>
        ) : null}
        {onReconnect ? (
          <button type="button" className={`vx-btn small${conn.needs_reconnect ? ' primary' : ''}`} onClick={() => onReconnect(conn)} data-testid="account-reconnect">
            Reconnect
          </button>
        ) : null}
        {conn.install_url ? (
          <a className="vx-btn small" href={conn.install_url} target="_blank" rel="noopener noreferrer">
            Choose repositories on GitHub
          </a>
        ) : null}
        {conn.manage_url && !compact ? (
          <a className="vx-btn small" href={conn.manage_url} target="_blank" rel="noopener noreferrer">
            Manage at {conn.provider_label}
          </a>
        ) : null}
        {onDisconnect ? (
          <button type="button" className="vx-btn small danger" onClick={() => onDisconnect(conn)} data-testid="account-disconnect">
            Disconnect
          </button>
        ) : null}
      </div>
    </div>
  );
}
